/**
 * The vendor mapping grids carry a row's price history as one line of text, most recent
 * first, written by the backend in a fixed shape per entry:
 *
 *   <price>[ <currency>][ from <yyyy-mm-dd>][ to <yyyy-mm-dd>]
 *
 * joined with " | ". That text is what the grid, the export and the reporting agent show.
 * The detail view wants the pieces, so this reads them back out of the same line rather
 * than asking the server for a second shape of the same data.
 */
export type PriceHistoryEntry = {
    price: string;
    currency: string | null;
    /** yyyy-mm-dd, or null on an entry the brand loaded from undated records. */
    from: string | null;
    to: string | null;
};

const ENTRY = /^(\S+)(?: ([A-Z]{3}))?(?: from (\d{4}-\d{2}-\d{2}))?(?: to (\d{4}-\d{2}-\d{2}))?$/;

/** Entries in the order the text lists them: most recent first. */
export const parsePriceHistory = (text: string | null | undefined): PriceHistoryEntry[] =>
    (text ?? '')
        .split(' | ')
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => {
            const m = ENTRY.exec(part);
            if (!m) return { price: part, currency: null, from: null, to: null };
            return { price: m[1], currency: m[2] ?? null, from: m[3] ?? null, to: m[4] ?? null };
        });

/** "12 Sep 2026 to 2 Nov 2026", "until 2 Nov 2026", or "undated". */
export const describePeriod = (entry: Pick<PriceHistoryEntry, 'from' | 'to'>): string => {
    if (entry.from && entry.to) return `${entry.from} to ${entry.to}`;
    if (entry.to) return `until ${entry.to}`;
    if (entry.from) return `from ${entry.from}`;
    return 'undated';
};
